import clsx from 'clsx';
import CircleTick from 'images/icons/CircleTick.svg';
import ImageIcon from 'images/icons/Image.svg';
import Location from 'images/icons/Location.svg';
import { lazy, Suspense } from 'preact/compat';
import { Review } from 'types/review';
import { ExtendedCarouselWidgetSettings } from 'types/settings';
import { useImage } from 'utilities/content';
import { useToggle } from 'utilities/use-toggle';

import { Icon } from 'components/Icon';
import { Image } from 'components/Image';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Stars } from 'components/Stars';
import { Text } from 'components/Text';

import styles from './VerticalSlide.module.scss';

const ReviewModal = lazy(() => import('components/ReviewModal'));

interface VerticalSlideProps {
  review: Review;
}

export function VerticalSlide({ review }: VerticalSlideProps) {
  const {
    title,
    author,
    rating,
    location,
    body,
    images,
    product_title,
    product_image_src,
    verified_purchase,
  } = review;

  const {
    show_location,
    show_thumbnail,
    hide_images,
    enable_show_full_review,
  } = useSettings<ExtendedCarouselWidgetSettings>();

  const {
    value: open,
    setTrue: openModal,
    setFalse: closeModal,
  } = useToggle(false);

  const { getImageUrl } = useImage();

  const coverImage = images.length
    ? getImageUrl(images[0].image_key, '400')
    : null;

  const isHideImages = !!hide_images;

  const classname = clsx(
    styles.VerticalSlide,
    !coverImage && styles.noImage,
    isHideImages && styles.HideImage,
  );

  const reviewTitleMarkup = title && (
    <Text variation="strong" lineClamp={2}>
      {title}
    </Text>
  );

  const productMarkup = (
    <Stack spacing="tight" alignment="center" wrap={false}>
      {show_thumbnail && (
        <Image
          size="medium"
          source={product_image_src || ImageIcon}
          alt={title}
        />
      )}
      <Stack.Item fill>
        <Text underline truncate variation="semiStrong" size="small">
          {product_title}
        </Text>
      </Stack.Item>
    </Stack>
  );

  const locationMarkup = show_location && location && (
    <Stack spacing="extraTight" alignment="center">
      <Icon source={Location} color="default" />
      <Text size="small">{location}</Text>
    </Stack>
  );

  const authorMarkup = (
    <Stack wrap={false}>
      <Stack.Item fill>
        <Stack spacing="extraTight" alignment="center" wrap={false}>
          <Text truncate size="small" variation="strong">
            {author}
          </Text>
          {verified_purchase === 'VERIFIED' && (
            <Icon source={CircleTick} color="default" />
          )}
        </Stack>
      </Stack.Item>
      {locationMarkup}
    </Stack>
  );

  const imageMarkup = !isHideImages && (
    <div className={styles.MediaImage}>
      <img
        loading="lazy"
        src={
          coverImage
            ? coverImage
            : 'data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHdpZHRoPSIyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0ibTI1IDEwYy0zLjk3ODIgMC03Ljc5MzYgMS41ODA0LTEwLjYwNjYgNC4zOTM0cy00LjM5MzQgNi42Mjg0LTQuMzkzNCAxMC42MDY2djE1MGMwIDMuOTc4IDEuNTgwNCA3Ljc5NCA0LjM5MzQgMTAuNjA3czYuNjI4NCA0LjM5MyAxMC42MDY2IDQuMzkzaDE1MGMzLjk3OCAwIDcuNzk0LTEuNTggMTAuNjA3LTQuMzkzczQuMzkzLTYuNjI5IDQuMzkzLTEwLjYwN3YtMTUwYzAtMy45NzgyLTEuNTgtNy43OTM2LTQuMzkzLTEwLjYwNjZzLTYuNjI5LTQuMzkzNC0xMC42MDctNC4zOTM0em01MCAzNWMxMSAwIDIwIDkgMjAgMjBzLTkgMjAtMjAgMjAtMjAtOS0yMC0yMCA5LTIwIDIwLTIwem04OS45OSAxMjVoLTEzMC4wMmMtNC4xIDAtNi40LTQuNi00LTcuOWwzNS41My00MC41MWMxLjktMi4xIDUuMi0yLjEgNy4yLS4xbDE2LjMgMTguNTEgMzAuNi00Ny44MWMuNDctLjY5MTMgMS4xMDUtMS4yNTM3IDEuODQ5LTEuNjM2Ljc0My0uMzgyMyAxLjU3LS41NzIyIDIuNDA1LS41NTIzLjgzNi4wMTk5IDEuNjUzLjI0OSAyLjM3Ny42NjYyLjcyNS40MTcyIDEuMzMzIDEuMDA5MyAxLjc2OSAxLjcyMjFsNDAuMzkgNzAuMTFjMS44IDMuNC0uNiA3LjUtNC40IDcuNXoiIGZpbGw9IiNFMEU3RUEiLz48L3N2Zz4='
        }
        alt={title}
      />
    </div>
  );

  const contentClasses = clsx(styles.Content, isHideImages && styles.HideImage);

  return (
    <>
      <Suspense fallback={null}>
        {open && (
          <ReviewModal
            review={review}
            contentExpanded={!enable_show_full_review}
            settings={{ show_location, show_thumbnail }}
            open={open}
            onClose={closeModal}
          />
        )}
      </Suspense>
      <div className={styles.SlideWrapper} onClick={openModal}>
        <div className={classname}>
          {imageMarkup}
          <div className={contentClasses}>
            <div className={styles.Stars}>
              <Stars value={rating} />
            </div>
            <Stack vertical alignment="fill">
              {authorMarkup}
              <Stack.Item fill>
                <Stack vertical spacing="tight">
                  {reviewTitleMarkup}
                  <Text size="small" lineClamp={3}>
                    {body}
                  </Text>
                </Stack>
              </Stack.Item>
              {productMarkup}
            </Stack>
          </div>
        </div>
      </div>
    </>
  );
}
